/**
 * YoYKpiCards
 *
 * 4 gradient KPI cards comparing before/after billing periods.
 * Each card shows: main value (after), "Before: X" sub-value, delta %, and direction arrow.
 * When before data is null (attorney not at firm in prior year),
 * shows "Before: N/A" and a "New" badge instead of a delta.
 */

import { YoYSummary, YoYGranularity } from "../../types/dashboard";
import { KPI_CARD_STYLES } from "../../config/dashboardTheme";

interface YoYKpiCardsProps {
  summary: YoYSummary;
  granularity: YoYGranularity;
}

interface CardProps {
  label: string;
  afterValue: number;
  beforeValue: number | null;
  format: "money" | "hours";
  gradient: string;
}

function formatValue(value: number, format: "money" | "hours"): string {
  if (format === "money") {
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return value.toFixed(1);
}

function KpiCard({ label, afterValue, beforeValue, format, gradient }: CardProps) {
  const hasBeforeData = beforeValue !== null;
  const delta = !hasBeforeData
    ? null
    : beforeValue === 0
      ? (afterValue > 0 ? 100 : 0)
      : ((afterValue - beforeValue) / beforeValue) * 100;
  const isPositive = delta !== null && delta >= 0;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br ${gradient} p-4 min-w-[150px]`}
    >
      <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <p className="text-xs font-medium text-white/70 mb-1">{label}</p>
      <p className="text-2xl font-bold text-white">
        {formatValue(afterValue, format)}
        {format === "hours" && (
          <span className="text-sm font-normal text-white/70 ml-1">hrs</span>
        )}
      </p>

      {/* Before comparison */}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-white/60">
          Before: {hasBeforeData ? formatValue(beforeValue, format) : "N/A"}
        </span>
        {delta !== null ? (
          <span
            className={`text-xs font-medium px-1.5 py-0.5 rounded ${
              isPositive
                ? "bg-green-500/20 text-green-300"
                : "bg-red-500/20 text-red-300"
            }`}
          >
            {isPositive ? "▲" : "▼"} {Math.abs(delta).toFixed(0)}%
          </span>
        ) : (
          <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">
            New
          </span>
        )}
      </div>
    </div>
  );
}

export default function YoYKpiCards({ summary, granularity }: YoYKpiCardsProps) {
  const before = summary.before;
  const after = summary.after;
  const hasBefore = before !== null;

  // "daily" falls back to monthly averages — the backend's YoYPeriodSummary
  // has no daily-average fields, so monthly is the best available approximation.
  const periodLabel = granularity === "weekly" ? "Weekly" : "Monthly";
  const avgBilledBefore = !hasBefore ? null
    : granularity === "weekly" ? before.avg_weekly_billed : before.avg_monthly_billed;
  const avgBilledAfter = granularity === "weekly"
    ? (after?.avg_weekly_billed ?? 0)
    : (after?.avg_monthly_billed ?? 0);
  const avgHoursBefore = !hasBefore ? null
    : granularity === "weekly" ? before.avg_weekly_hours : before.avg_monthly_hours;
  const avgHoursAfter = granularity === "weekly"
    ? (after?.avg_weekly_hours ?? 0)
    : (after?.avg_monthly_hours ?? 0);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <KpiCard
        label="Total Billed"
        afterValue={after?.total_billed ?? 0}
        beforeValue={hasBefore ? before.total_billed : null}
        format="money"
        gradient={KPI_CARD_STYLES.green}
      />
      <KpiCard
        label={`Avg ${periodLabel} Billings`}
        afterValue={avgBilledAfter}
        beforeValue={avgBilledBefore}
        format="money"
        gradient={KPI_CARD_STYLES.green}
      />
      <KpiCard
        label="Total Hours"
        afterValue={after?.total_hours ?? 0}
        beforeValue={hasBefore ? before.total_hours : null}
        format="hours"
        gradient={KPI_CARD_STYLES.blue}
      />
      <KpiCard
        label={`Avg ${periodLabel} Hours`}
        afterValue={avgHoursAfter}
        beforeValue={avgHoursBefore}
        format="hours"
        gradient={KPI_CARD_STYLES.blue}
      />
    </div>
  );
}
